import { forwardRef, ImgHTMLAttributes, useState } from 'react'
import { IconDownload } from '@tabler/icons-react'

import { getMessageImageStoragePath } from '@/lib/media/safe-url'
import { cn } from '@/lib/utils'
import { BlurFade } from '@/components/ui/blur-fade'
import { ImageModal } from '@/components/ui/image-modal'

import { Button } from '../ui/button'

const ImageWithPreview = forwardRef<
  HTMLImageElement,
  ImgHTMLAttributes<HTMLImageElement> & { isStreaming?: boolean }
>(({ src, alt = 'File image', className, isStreaming, ...props }, ref) => {
    const [showImageModal, setShowImageModal] = useState(false)

    const [isDownloading, setIsDownloading] = useState(false)
    const handleDownload = async () => {
      if (!src) return
      setIsDownloading(true)
      try {
        const storagePath = getMessageImageStoragePath(src)
        const downloadUrl = storagePath
          ? `/api/image/download?path=${encodeURIComponent(storagePath)}`
          : `/api/image/download?url=${encodeURIComponent(src)}`
        const response = /^data:|^blob:/i.test(src)
          ? await fetch(src)
          : await fetch(downloadUrl)
        if (!response.ok) throw new Error('Network response was not ok')
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `Losi_image_${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('Download failed:', error)
        // You might want to show an error message to the user here
      } finally {
        setIsDownloading(false)
      }
    }

    // While the message is still streaming, the surrounding markdown re-parses
    // on every token, which remounts this <img> and replays the blur/fade —
    // causing the image to flash repeatedly. Render statically during
    // streaming and only run the entrance animation once the stream settles.
    const imgEl = (
      /* eslint-disable-next-line @next/next/no-img-element */
      <img
        ref={ref}
        onClick={() => setShowImageModal(true)}
        className={cn(
          'my-0 h-auto max-h-[min(70dvh,640px)] max-w-full cursor-pointer rounded-lg object-contain',
          !isStreaming && 'animate-blur',
          className
        )}
        src={src || ''}
        alt={alt}
        {...props}
      />
    )

    return (
      <span className={cn('relative inline-flex max-w-full cursor-pointer overflow-hidden rounded-lg align-top')}>
        {isStreaming ? (
          imgEl
        ) : (
          <BlurFade yOffset={0} key={src} delay={0.25 * 0.05} inView>
            {imgEl}
          </BlurFade>
        )}
        <Button
          loading={isDownloading}
          disabled={isDownloading}
          className={cn(
            'absolute right-2 top-2 size-8 cursor-pointer rounded-md bg-black/30 p-1 text-white transition-opacity duration-200'
          )}
          onClick={isDownloading ? undefined : handleDownload}
        >
          <IconDownload stroke={1.5} size={18} />
        </Button>
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          src={src || ''}
          alt={alt}
        />
      </span>
    )
  }
)

ImageWithPreview.displayName = 'ImageWithPreview'

export { ImageWithPreview }
